import { useState } from "react";
import { PlayCircle } from "lucide-react";

/**
 * Hero video / speaker reel section.
 * Uses click-to-play pattern: shows high-res YouTube thumbnail until
 * user clicks play, then loads the iframe with autoplay.
 */
const VIDEOS = [
  {
    id: "4kfLslYBqa4",
    title: "Let's Tandem — Gary Zelesky",
    label: "Featured Interview",
    embeddable: true,
  },
  {
    id: "tZSX7A31LK0",
    title: "Gary Live · Stage Moments",
    label: "Speaker Reel",
    startAt: 4,
    embeddable: false, // YouTube blocks embed (Error 153)
  },
  {
    id: "f9ZLuMO1JaI",
    title: "Queen Bee of Speakers",
    label: "Audience Reaction",
    embeddable: false, // YouTube Short — cannot embed
  },
  {
    id: "tdQEVnsaE_E",
    title: "She Stood in Line for 3 Hours",
    label: "13 Years Later",
    embeddable: false, // YouTube Short — cannot embed
  },
];

const embedUrl = (v) =>
  `https://www.youtube.com/embed/${v.id}?autoplay=1&rel=0&modestbranding=1${v.startAt ? `&start=${v.startAt}` : ""}`;

const watchUrl = (v) =>
  `https://www.youtube.com/watch?v=${v.id}${v.startAt ? `&t=${v.startAt}s` : ""}`;

// Use maxresdefault for the main player (1280x720) and hqdefault for thumbs
const hiResThumb = (id) => `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;
const thumb = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

export default function VideoReel() {
  const [active, setActive] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const current = VIDEOS[active];

  const playVideo = (i) => {
    const v = VIDEOS[i];
    if (!v.embeddable) {
      // YouTube blocks embedding for this video (Short or Content ID restriction).
      // Open the official watch page in a new tab where playback always works.
      window.open(watchUrl(v), "_blank", "noopener,noreferrer");
      return;
    }
    setActive(i);
    setIsPlaying(true);
  };

  // No effect needed — playVideo controls both active and isPlaying together.

  return (
    <section
      id="watch"
      data-testid="video-reel-section"
      className="relative bg-[#050505] py-24 md:py-32 border-t border-[#262626]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="section-num">WATCH · SPEAKER REEL</span>
          <div className="divider-red" />
        </div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <h2 className="h-display text-5xl md:text-6xl lg:text-7xl max-w-3xl">
            See Gary <br />
            <span className="text-[#EC1985]">in action.</span>
          </h2>
          <p className="text-white/70 text-lg max-w-md">
            Watch real footage from real events — so you know exactly what
            your audience is about to experience.
          </p>
        </div>

        {/* Main video player */}
        <div className="relative aspect-video w-full bg-[#0a0a0a] border border-[#262626] overflow-hidden">
          {isPlaying && current.embeddable ? (
            <iframe
              key={current.id}
              data-testid="video-iframe"
              src={embedUrl(current)}
              title={current.title}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <button
              onClick={() => {
                if (!current.embeddable) {
                  window.open(watchUrl(current), "_blank", "noopener,noreferrer");
                  return;
                }
                setIsPlaying(true);
              }}
              data-testid="main-player-play"
              aria-label={`Play ${current.title}`}
              className="absolute inset-0 w-full h-full group cursor-pointer"
            >
              {/* Try maxres first, fall back to hqdefault */}
              <img
                src={hiResThumb(current.id)}
                onError={(e) => {
                  if (e.target.src.includes("maxresdefault")) {
                    e.target.src = thumb(current.id);
                  }
                }}
                alt={`${current.title} — click to play`}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/60 via-transparent to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-[#EC1985] rounded-full p-7 shadow-[0_0_80px_rgba(236,25,133,0.5)] group-hover:scale-110 transition-transform duration-300">
                  <PlayCircle size={72} strokeWidth={1.5} className="text-white" />
                </div>
              </div>
              {!current.embeddable && (
                <div className="absolute top-4 right-4 bg-[#EC1985] text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-full">
                  Plays on YouTube ↗
                </div>
              )}
              <div className="absolute bottom-6 left-6 right-6 text-left">
                <div className="text-xs uppercase tracking-[0.3em] text-[#EC1985] font-bold mb-2">
                  {current.label}
                </div>
                <div className="font-display text-2xl md:text-3xl text-white">
                  {current.title}
                </div>
              </div>
            </button>
          )}
        </div>

        {/* Currently selected label */}
        <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#EC1985]">
              {isPlaying ? "Now Playing" : "Up Next"}
            </div>
            <div className="text-sm font-bold text-white">{current.title}</div>
          </div>
          <a
            href={`https://www.youtube.com/watch?v=${current.id}${current.startAt ? `&t=${current.startAt}s` : ""}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="watch-on-youtube-fallback"
            className="text-xs uppercase tracking-[0.2em] font-bold text-white/60 hover:text-[#EC1985] transition-colors flex items-center gap-2"
          >
            Trouble playing? Watch on YouTube
            <span aria-hidden="true">↗</span>
          </a>
        </div>

        {/* Video switcher row */}
        {VIDEOS.length > 1 && (
          <div className="mt-8">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-white/50 mb-4">
              More Videos
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {VIDEOS.map((v, i) => {
                const isActive = i === active;
                return (
                  <button
                    key={v.id}
                    onClick={() => playVideo(i)}
                    data-testid={`video-thumb-${i}`}
                    className={`group relative aspect-video overflow-hidden text-left border transition-colors ${
                      isActive
                        ? "border-[#EC1985] ring-2 ring-[#EC1985]/40"
                        : "border-[#262626] hover:border-[#EC1985]"
                    }`}
                  >
                    <img
                      src={thumb(v.id)}
                      alt={v.title}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className={`rounded-full p-3 transition-transform group-hover:scale-110 ${
                        isActive ? "bg-[#EC1985]" : "bg-black/70 group-hover:bg-[#EC1985]"
                      }`}>
                        <PlayCircle size={28} className="text-white" />
                      </div>
                    </div>
                    {!v.embeddable && (
                      <div className="absolute top-2 right-2 bg-[#EC1985] text-white text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-1 rounded-full">
                        YouTube ↗
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-[#EC1985] font-bold mb-1">
                        {v.label}
                      </div>
                      <div className="text-sm font-bold text-white">{v.title}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-6 text-xs uppercase tracking-[0.2em] text-white/40 font-bold">
          ★ Additional event footage and full keynote recordings available on request{" "}
          <a
            href="mailto:Cherisse@GaryZelesky.com?subject=Request%20Full%20Keynote%20Footage"
            className="text-[#EC1985] hover:text-white underline ml-1"
            data-testid="request-full-footage"
          >
            Email Cherisse →
          </a>
        </div>
      </div>
    </section>
  );
}
